/**
 * monitoring-actions.ts — pure action logic for the Baseline Compliance
 * Monitoring tab's self-service setup journey (Phase 2,
 * `.agents/Tasks/High Priority/tcm-coverage-phase2-self-service-blueprint.md` §6).
 *
 * The component test harness is renderToStaticMarkup + mocked useQuery —
 * effects never run — so EVERY piece of conditional logic and EVERY string a
 * customer can see lives HERE, not in MonitoringTab.tsx. The component only
 * wires mutations and renders these view models. Vitest coverage in
 * monitoring-actions.test.ts, including the FORBIDDEN-jargon sweep over every
 * composed string.
 *
 * Voice rules (CLAUDE.md Product Voice + monitoring-coverage.ts header):
 *   - Trusted advisor: verdicts first, honest states, no apologies.
 *   - NO internal jargon, ever: TCM / DSC / UTCM / "snapshot" / budgets /
 *     quotas / ceilings / "/200" must not appear in any string this module
 *     emits. "Capture" and "coverage" are the customer words.
 *   - Never fabricate: a missing count renders as silence, not a 0; raw
 *     server ProblemDetails `detail` NEVER reaches the customer surface —
 *     every write error routes through mapWriteError.
 */

import { format } from 'date-fns'

import type {
  TcmCoverageSummary,
  TcmEnablementVerifyPayload,
  TcmEnablementVerifyResult,
  TcmLicenseEligibility,
  TcmMonitoringGroup,
} from '@/types/tcm'
import { formatSettingCount } from './monitoring-coverage'

/* -------------------------------------------------------------------------- */
/* Wire DTO types for the six /tcm/coverage/{tenantId}/* journey endpoints     */
/* (blueprint §4 — customer-shaped, camelCase per System.Text.Json).           */
/* -------------------------------------------------------------------------- */

/** 202 body of POST …/size. */
export interface TcmSizingStartResult {
  jobId: string
  status: string
}

/** One monitoring group's instance count from the archived sizing capture. */
export interface TcmSizedGroup {
  group: TcmMonitoringGroup
  instanceCount: number
}

/**
 * Body of GET …/size/{jobId} (blueprint §4.4 / D1). Customer-shaped status:
 * no raw pipeline statuses on the wire. `sizedGroups` is null until the
 * capture is archived — never an empty fabricated list.
 */
export interface TcmSizingStatus {
  jobId: string
  status: 'sizing' | 'sized' | 'failed'
  archived: boolean
  sizedGroups: TcmSizedGroup[] | null
}

/** 202 body of POST …/scope. */
export interface TcmScopeResult {
  monitorVersionId: string
  instanceCount: number
  typeCount: number
}

/* -------------------------------------------------------------------------- */
/* parseVerificationOutput — tolerant parse of the script's pasted output      */
/* -------------------------------------------------------------------------- */

export type VerificationParseResult =
  | { ok: true; payload: TcmEnablementVerifyPayload; token: string }
  | { ok: false; reason: string }

/** Customer-voiced parse-failure reasons (exported for the jargon sweep). */
export const PARSE_REASONS = {
  empty: 'Paste the output the setup script printed when it finished.',
  unreadable:
    'That doesn’t look like the script’s output. Copy the full block it printed at the end of its run and paste it whole.',
  partial:
    'That looks like only part of the output. Copy everything the script printed, start to finish, and paste it whole.',
} as const

/**
 * Tolerant parse of the verification blob the enablement script prints at its
 * final step: `{ "payload": { … }, "token": "…" }`. Accepts the blob embedded
 * in surrounding console noise (we slice from the first `{` to the last `}`).
 * Never throws — garbage degrades to an honest, customer-voiced reason.
 */
export function parseVerificationOutput(pasted: string): VerificationParseResult {
  const text = (pasted ?? '').trim()
  if (text.length === 0) {
    return { ok: false, reason: PARSE_REASONS.empty }
  }

  // Try the raw text first, then the brace-bounded slice (script output is
  // often pasted with prompt/banner noise around it).
  const candidates: string[] = [text]
  const first = text.indexOf('{')
  const last = text.lastIndexOf('}')
  if (first !== -1 && last > first) {
    const sliced = text.slice(first, last + 1)
    if (sliced !== text) candidates.push(sliced)
  }

  let parsed: unknown = undefined
  for (const candidate of candidates) {
    try {
      parsed = JSON.parse(candidate)
      break
    } catch {
      // try the next candidate
    }
  }

  if (parsed === undefined || parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, reason: PARSE_REASONS.unreadable }
  }

  const obj = parsed as Record<string, unknown>
  const payload = obj.payload
  const token = obj.token
  const hasPayload = !!payload && typeof payload === 'object' && !Array.isArray(payload)
  const hasToken = typeof token === 'string' && token.trim().length > 0

  if (hasPayload && hasToken) {
    return { ok: true, payload: payload as TcmEnablementVerifyPayload, token: (token as string).trim() }
  }

  // Parsed as JSON but missing one half of the contract — the user most
  // likely copied a fragment of the printed block.
  return { ok: false, reason: PARSE_REASONS.partial }
}

/* -------------------------------------------------------------------------- */
/* composeJourneyActions — which step is actionable now                        */
/* -------------------------------------------------------------------------- */

export type JourneyActionId = 'connect' | 'verify' | 'size' | 'scope' | 'activate'

export interface JourneyAction {
  id: JourneyActionId
  /** The journey step this action renders inside ('activate' lives on 'watch'). */
  stepId: 'connect' | 'verify' | 'size' | 'scope' | 'watch'
  /** Primary button label. */
  label: string
  /** One sentence of helper copy rendered above the control. */
  helper: string
  /** False renders the button inert with `disabledReason` beside it. */
  enabled: boolean
  /** Honest reason when not enabled; null when enabled. */
  disabledReason: string | null
}

export interface JourneyActions {
  /** The single actionable step right now; null once monitoring is Active. */
  current: JourneyAction | null
  /**
   * True on the verify step — the admin can request a fresh script without
   * losing journey progress (each script is good for one day).
   */
  canRequestFreshScript: boolean
}

/**
 * Pure function of the coverage DTO: which journey step carries an action
 * right now, its label, and its helper copy. The Sized state forks on the
 * active-watch-list signal (`watchedInstanceCount != null`): scope not yet
 * chosen → choose coverage; chosen → the watch step's switch-on action.
 */
export function composeJourneyActions(coverage: TcmCoverageSummary): JourneyActions {
  const none: JourneyActions = { current: null, canRequestFreshScript: false }

  // ExoProvisioning (mail coverage still finishing) is ALWAYS a customer-driven
  // run 2 — the read-only Guardian service principal deliberately can't create
  // the Exchange management-role assignment itself, so there is NO server
  // auto-finish for it. The admin must re-download, re-run, and re-paste the
  // mail-included script. Surface the verify/run-2 affordance up front — BEFORE
  // the monitoringActive short-circuit, because the Graph side is typically
  // already live (monitoringActive true) while mail is still provisioning — so
  // the affordance never disappears behind the non-interactive finish rung and a
  // tenant can't reach Hybrid with mail silently incomplete.
  if (coverage.onboarding.exoProvisioning) {
    return {
      current: {
        id: 'verify',
        stepId: 'verify',
        label: 'Finish mail coverage',
        helper:
          'Mail coverage needs one more run to finish setup. Have your Microsoft 365 admin run the mail-coverage script, then paste its output here and verify.',
        enabled: true,
        disabledReason: null,
      },
      // The customer needs the mail-included script (the follow-up download in
      // the verify panel), not a fresh standard script — so no fresh-script link.
      canRequestFreshScript: false,
    }
  }

  if (coverage.monitoringActive) return none

  switch (coverage.onboarding.state) {
    case 'NotStarted':
      return {
        current: {
          id: 'connect',
          stepId: 'connect',
          label: 'Get the setup script',
          helper:
            'Your Microsoft 365 admin runs it in about ten minutes. It sets up the enterprise apps Guardian needs and grants them the roles and permissions in the permissions guide, most of them read-only. Apart from turning on Exchange organization customization if it’s off, and removing the Compliance Administrator role from Microsoft’s M365 Tenant Configuration Manager app unless your setup includes Purview compliance monitoring, it doesn’t change your policies or settings.',
          enabled: true,
          disabledReason: null,
        },
        canRequestFreshScript: false,
      }
    case 'ScriptReady':
      return {
        current: {
          id: 'verify',
          stepId: 'verify',
          label: 'Verify the connection',
          helper:
            'Paste the output the script printed when it finished, and Guardian checks the connection end to end.',
          enabled: true,
          disabledReason: null,
        },
        canRequestFreshScript: true,
      }
    case 'Connected':
      return {
        current: {
          id: 'size',
          stepId: 'size',
          label: 'Size my coverage',
          helper:
            'We take a full capture of your configuration so coverage reflects what actually exists. It usually finishes within a few minutes.',
          enabled: true,
          disabledReason: null,
        },
        canRequestFreshScript: false,
      }
    case 'Sized':
      if (coverage.watchedInstanceCount != null) {
        // Scope already chosen (an active watch list exists) — the remaining
        // move is the switch-on, which lives on the Watch step.
        return {
          current: {
            id: 'activate',
            stepId: 'watch',
            label: 'Switch on continuous watch',
            helper:
              'Your coverage is chosen. One click and changes start landing in the queue, ranked and explained.',
            enabled: true,
            disabledReason: null,
          },
          canRequestFreshScript: false,
        }
      }
      return {
        current: {
          id: 'scope',
          stepId: 'scope',
          label: 'Start watching this coverage',
          helper: 'Pick the coverage that fits. You can widen it any time.',
          enabled: true,
          disabledReason: null,
        },
        canRequestFreshScript: false,
      }
    case 'Active':
    default:
      return none
  }
}

/* -------------------------------------------------------------------------- */
/* "Setting up" rung + GAP-6 held-state (W9 — self-serve auto-finish)          */
/* -------------------------------------------------------------------------- */

/**
 * The poll-driven finish phase for a self-serve tenant, derived from the
 * server `onboarding` overlay (W5b D-1, server commit 8ebbfa4):
 *
 *  - 'setting-up' — the server is auto-driving the finish (size → scope →
 *    activate) and/or the monitor is live but the deferred born-Hybrid promote
 *    has not flipped BaselineSource to Hybrid yet. A non-interactive,
 *    poll-driven rung; no manual buttons. Reached when `finishState` is
 *    'Finishing' (preferred signal) OR the server capped `state` at 'Finishing'.
 *  - 'held' — the resumable finish parked with a reason (e.g. the chosen scope
 *    can't fit, a capture allowance ran out, or a transient resolution blip).
 *    No customer action satisfies it — Guardian picks it up — so the rung says
 *    so honestly and never shows a re-verify prompt (the verify seed is gone).
 *  - null — not a self-serve finish tenant: the existing interactive journey
 *    (composeJourneyActions) owns the rung, or monitoring is already Active.
 *
 * Keyed off the server overlay only — an operator/PA tenant (no marker) always
 * has `finishState === null`, so this never fires for them.
 */
export type FinishPhase = 'setting-up' | 'held' | null

export function classifyFinishPhase(coverage: TcmCoverageSummary): FinishPhase {
  const { finishState, state, exoProvisioning } = coverage.onboarding
  // ExoProvisioning is never a passive server-auto-finish: mail coverage requires
  // a customer-driven run 2 (the read-only service principal can't self-assign the
  // Exchange role), so it must keep the interactive verify affordance, not the
  // "nothing to do" rung. The server still stamps finishState="Finishing" for a
  // marked self-serve tenant in this window (the cutover marker is set the whole
  // pre-Hybrid span), so we exclude ExoProvisioning explicitly here — otherwise
  // the finish rung would swallow the run-2 controls and mail would reach Hybrid
  // silently incomplete. Enabled-path finishes are exoProvisioning=false and keep
  // the passive rung.
  if (exoProvisioning) return null
  // The dedicated overlay field is the contract; the capped `state` is the
  // belt-and-braces fallback for a payload that set one but not the other.
  if (finishState === 'Held' || state === 'Held') return 'held'
  if (finishState === 'Finishing' || state === 'Finishing') return 'setting-up'
  return null
}

export interface SettingUpRung {
  phase: Exclude<FinishPhase, null>
  /** Six-words-or-fewer verdict, leading the rung. */
  headline: string
  /** One supporting sentence under the headline. */
  body: string
  /**
   * Quiet, honest hold detail rendered beneath the body on the held rung —
   * the server's customer-voiced reason when present, else null (the rung
   * reads complete without it). Never the headline; never a raw error.
   */
  detail: string | null
  /**
   * True only on the setting-up phase when the live coverage poll has given up
   * (>= COVERAGE_POLL_MAX_FAILURES consecutive failures): the page has stopped
   * auto-updating, so the panel drops the "keeps itself up to date" promise and
   * offers a manual re-check instead of a spinner that no longer means "live".
   * Always false on the held phase (terminal; the human path owns it).
   */
  staleConnection: boolean
}

/**
 * View-model for the non-interactive "Setting up" / held rung. Returns null
 * when the tenant is not in a self-serve finish phase (the interactive journey
 * owns the rung then). All copy is advisor-voiced and jargon-free; the held
 * detail passes through the server's already-customer-voiced reason verbatim
 * as a SUPPORTING clause, so a server-side reason that somehow carried jargon
 * still can't become the verdict.
 */
export function composeSettingUpRung(
  coverage: TcmCoverageSummary,
  pollStalled = false,
): SettingUpRung | null {
  const phase = classifyFinishPhase(coverage)
  if (phase === null) return null

  if (phase === 'held') {
    const reason = coverage.onboarding.holdReason?.trim()
    return {
      phase,
      headline: 'Almost there. We’re finishing by hand.',
      body:
        'Your connection is in and your coverage is being set up. One part needs a person, ' +
        'so your Guardian team is finishing it for you. Nothing more is needed from you.',
      detail: reason && reason.length > 0 ? reason : null,
      staleConnection: false,
    }
  }

  // setting-up: the finish chain advances on its own and this page polls to keep
  // up — UNLESS the poll has given up after repeated failures, in which case the
  // page has silently stopped updating. Tell the truth and offer a manual re-check
  // rather than a spinner promising a self-update that no longer happens.
  if (pollStalled) {
    return {
      phase,
      headline: 'Setting up your continuous watch.',
      body:
        'Your connection is verified and Guardian is finishing setup on its own. ' +
        'We’ve lost the live connection to this page, so it has stopped updating itself. ' +
        'Use Check again, or reload the page.',
      detail: null,
      staleConnection: true,
    }
  }

  return {
    phase,
    headline: 'Setting up your continuous watch.',
    body:
      'Your connection is verified and Guardian is sizing your coverage and switching on the watch. ' +
      'This finishes on its own, usually within a few hours, and this page keeps itself up to date. ' +
      'You don’t need to do anything.',
    detail: null,
    staleConnection: false,
  }
}

/* -------------------------------------------------------------------------- */
/* coverageRefetchInterval — bounded poll for the auto-finish window           */
/* -------------------------------------------------------------------------- */

/** Poll cadence (ms) while the self-serve auto-finish chain is in flight. */
export const COVERAGE_POLL_INTERVAL_MS = 8_000
/**
 * Consecutive coverage-poll failures after which we stop polling rather than
 * spin forever over a dead endpoint — mirrors the sizing poll's 3-failure cap.
 */
export const COVERAGE_POLL_MAX_FAILURES = 3

/**
 * Which onboarding states keep the coverage poll armed. The auto-chain advances
 * the server-computed state without any client action, so we poll through it:
 *  - Connected / Sized — the server is auto-driving size → scope → activate
 *    (the customer sees the "Setting up" rung, no manual buttons).
 *  - Finishing — the monitor may be live but the deferred born-Hybrid promote
 *    hasn't flipped to Hybrid yet; we poll until the server reports Active.
 * We do NOT poll on:
 *  - NotStarted / ScriptReady — the journey waits on a manual action (script
 *    run / paste-and-verify), not a background chain; polling would be noise.
 *  - Held — terminal until a person intervenes; nothing to poll for.
 *  - Active — the finish completed; the channels tell the story.
 */
const POLLED_FINISH_STATES: ReadonlySet<TcmCoverageSummary['onboarding']['state']> = new Set([
  'Connected',
  'Sized',
  'Finishing',
])

/**
 * Bounded `refetchInterval` for the coverage query. Returns the poll cadence
 * while the auto-finish chain is in flight, or `false` to stop once the tenant
 * reaches a finished/held/manual-wait state OR the poll has failed too many
 * times in a row. Pure over the inputs react-query's `refetchInterval` callback
 * already has (the latest data + the consecutive failure count), so the whole
 * cadence decision is unit-testable without effects.
 */
export function coverageRefetchInterval(
  coverage: TcmCoverageSummary | undefined,
  consecutiveFailureCount: number,
): number | false {
  if (consecutiveFailureCount >= COVERAGE_POLL_MAX_FAILURES) return false
  if (!coverage) return false
  // monitoringActive is the live-end-to-end signal; once it's true the finish
  // is done from the channels' point of view even if state briefly lags.
  if (coverage.monitoringActive && coverage.onboarding.state === 'Active') return false
  return POLLED_FINISH_STATES.has(coverage.onboarding.state) ? COVERAGE_POLL_INTERVAL_MS : false
}

/* -------------------------------------------------------------------------- */
/* mapWriteError — customer-voiced copy for write failures                     */
/* -------------------------------------------------------------------------- */

/**
 * Customer-voiced copy for the known 409/400 families. NEVER render raw
 * ProblemDetails `detail` to a customer — it names internal pipeline objects.
 * Families are matched on the ProblemDetails `title` (stable, server-side):
 *   - quota exhausted ......... "Monthly snapshot resource quota exhausted"
 *   - over-capacity selection . "Monitor baseline exceeds the per-tenant monitoring budget"
 *   - staged changes pending .. "Staged accepts would be orphaned by a monitor refresh"
 *   - not ready to activate ... "Connection is not ready for monitoring"
 *   - scope not chosen ........ "No monitoring scope is in place"
 *   - nothing watchable ....... "All requested resource types were filtered out" /
 *                               "Profiles resolve to no monitorable types"
 *   - capture missing ......... "No usable snapshot found"
 * Unknown errors get honest generic retry copy.
 */
export function mapWriteError(status: number, problemTitle: string | null): string {
  const title = (problemTitle ?? '').toLowerCase()

  if (title.includes('quota')) {
    return (
      'We’ve taken our full set of configuration captures for this month. ' +
      'Sizing can run again at the start of next month, or your Guardian team can work from a recent capture.'
    )
  }
  if (title.includes('exceeds') || title.includes('budget') || title.includes('capacity')) {
    return (
      'That selection is more than continuous watch can hold for your tenant. ' +
      'Trim a coverage area and try again, or ask your Guardian team to tailor the scope with you.'
    )
  }
  if (title.includes('staged')) {
    return (
      'You have staged changes waiting to be applied. ' +
      'Apply or discard them in the queue first, then choose your coverage.'
    )
  }
  if (title.includes('filtered') || title.includes('no monitorable types')) {
    return (
      'None of that coverage can be watched for your tenant yet. ' +
      'Pick a different option, or ask your Guardian team what it would take to open it up.'
    )
  }
  if (title.includes('no usable')) {
    return 'Your configuration capture isn’t ready yet. Run the sizing step first, then choose your coverage.'
  }
  if (title.includes('no monitoring scope')) {
    return 'Your coverage isn’t chosen yet. Pick what Guardian should watch first, then switch on.'
  }
  if (title.includes('ready') || title.includes('activat')) {
    return (
      'Your tenant isn’t quite ready to switch on. ' +
      'Finish the earlier steps first. If everything looks done, give it a moment and try again.'
    )
  }

  if (status === 401) {
    return 'Your session needs a refresh. Sign in again and retry.'
  }
  if (status === 403) {
    return 'You don’t have access to make changes for this tenant.'
  }
  if (status === 404) {
    return 'We couldn’t find that setup for your tenant. Refresh the page and try again.'
  }

  return (
    'That didn’t go through, and nothing was changed. Try again in a moment. ' +
    'If it keeps happening, your Guardian team can take it from there.'
  )
}

/**
 * Convenience over mapWriteError for mutation onError handlers: pulls the
 * HTTP status + ProblemDetails title out of an axios-shaped error and maps
 * them. Anything unshaped (network failure, plain Error) gets the honest
 * generic copy. Pure — safe to unit test without axios.
 */
export function mapWriteErrorFrom(err: unknown): string {
  let status = 0
  let title: string | null = null
  if (err && typeof err === 'object') {
    const response = (err as { response?: unknown }).response
    if (response && typeof response === 'object') {
      const r = response as { status?: unknown; data?: unknown }
      if (typeof r.status === 'number') status = r.status
      if (r.data && typeof r.data === 'object') {
        const t = (r.data as { title?: unknown }).title
        if (typeof t === 'string') title = t
      }
    }
  }
  return mapWriteError(status, title)
}

/* -------------------------------------------------------------------------- */
/* mapVerifyResult — verdict copy for the three verification outcomes          */
/* -------------------------------------------------------------------------- */

export interface VerifyOutcome {
  tone: 'success' | 'caveat' | 'error'
  headline: string
  body: string
  /**
   * True only for the propagation-lag transient (isValid=false, status back at
   * ScriptGenerated): the same pasted output will work once Microsoft catches
   * up, so the wizard can silently re-check on a timer instead of asking the
   * customer to click Verify again. False for terminal outcomes (success,
   * caveat, and a genuine failure that needs a fresh script).
   */
  retriable: boolean
  /**
   * True only on the ExoProvisioning state: connected, but mail coverage is
   * still finishing and needs one more admin run. The wizard renders a
   * "Finish mail coverage" affordance that downloads the Exchange-included
   * script for the customer's Microsoft 365 admin to re-run — completing setup,
   * not an optional extra.
   */
  exchangeFollowUp?: boolean
}

/**
 * Maps the server's verification result to advisor-voiced verdict copy.
 *   - Enabled .......... full success.
 *   - ExoProvisioning .. caveat: connected, but mail coverage is required and
 *                        still finishing (one more admin run needed).
 *   - license floor .... (reason='LicenseIneligible') Microsoft requires a
 *                        qualifying plan first — actionable license guidance,
 *                        NOT retriable (waiting fixes nothing; the same paste
 *                        works again once a license lands).
 *   - transient retry .. (isValid=false, status back at ScriptGenerated) the
 *                        server kept the journey intact — same paste, try again.
 *   - anything else .... honest failure + fresh-script guidance.
 * The raw server `message` is operator material and never surfaces here.
 */
export function mapVerifyResult(result: TcmEnablementVerifyResult): VerifyOutcome {
  if (!result.isValid && result.reason === 'LicenseIneligible') {
    // Microsoft's tenant-level license floor. Checked BEFORE the generic
    // ScriptGenerated transient branch — the server reports this with the same
    // status (seed preserved, journey intact), and only `reason` tells them
    // apart. Not retriable: the silent auto-retry loop can't buy a license.
    return {
      tone: 'error',
      headline: 'Your Microsoft plan needs one more license first.',
      body:
        'Microsoft requires Microsoft 365 E3 or E5, Business Premium, or Microsoft Entra ID P1 before Guardian can watch your configuration. Your Microsoft admin can add one of those, and the free Microsoft Entra ID P1 trial in the Microsoft admin center works too. Once the license is in place, verify again with the same pasted output.',
      retriable: false,
    }
  }
  if (result.isValid && result.status === 'Enabled') {
    return {
      tone: 'success',
      headline: 'Connected. Every permission landed.',
      body:
        'Guardian checked the connection end to end. Next we size your coverage with a full capture of your configuration.',
      retriable: false,
    }
  }
  if (result.status === 'ExoProvisioning') {
    // A TRANSIENT, in-progress state — NOT a success terminal. The Graph side is
    // connected and identity/threat-protection/device settings are watched now,
    // but mail coverage is REQUIRED and still finishing setup: it needs one more
    // run by the customer's Microsoft 365 admin (exchangeFollowUp drives that
    // affordance). Framed as a caveat, never as done — mirrors the backend copy
    // at TcmEnablementService.cs (Exchange "still finishing setup").
    return {
      tone: 'caveat',
      headline: 'Connected. Mail coverage is still finishing.',
      body:
        'Guardian is watching your identity, threat-protection, and device settings now. Mail coverage isn’t complete yet. It needs one more run by your Microsoft 365 admin to finish setting up.',
      retriable: false,
      exchangeFollowUp: true,
    }
  }
  if (!result.isValid && result.status === 'ScriptGenerated') {
    // The check hit a transient bump server-side but the pasted output is
    // sound and the journey kept its place — the same paste works on retry.
    // Marked retriable so the wizard re-checks on a timer without a re-paste.
    return {
      tone: 'error',
      headline: 'That check didn’t go through.',
      body:
        'Nothing is wrong with the script’s output. Give it a moment and verify again with the same pasted output.',
      retriable: true,
    }
  }
  return {
    tone: 'error',
    headline: 'That check didn’t pass.',
    body:
      'Ask your Microsoft 365 admin to run a fresh setup script, then paste the new output here. Each script is good for one day.',
    retriable: false,
  }
}

/* -------------------------------------------------------------------------- */
/* Verify auto-retry — silent re-check for the propagation-lag transient       */
/* -------------------------------------------------------------------------- */

/**
 * Cadence for the wizard's silent verify auto-retry. On a `retriable` outcome
 * (Microsoft still propagating the freshly-granted access) the wizard re-checks
 * with the SAME pasted output every `intervalMs` for up to `maxAttempts` before
 * falling back to the manual "verify again" prompt. ~45s × 4 ≈ 3 minutes, which
 * covers the propagation lag observed in practice without spinning forever.
 */
export const VERIFY_AUTO_RETRY = {
  intervalMs: 45_000,
  maxAttempts: 4,
} as const

/**
 * Pure predicate: should the wizard schedule another silent verify re-check?
 * True only while the last outcome is retriable AND we haven't exhausted the
 * attempt budget. Kept here (not in the component) so the cadence decision is
 * unit-testable without effects — same seam discipline as the rest of the file.
 */
export function shouldAutoRetryVerify(
  outcome: VerifyOutcome | null,
  attemptsSoFar: number,
): boolean {
  return !!outcome?.retriable && attemptsSoFar < VERIFY_AUTO_RETRY.maxAttempts
}

/**
 * Calm, advisor-voiced copy shown while a silent verify re-check is in flight,
 * in place of the transient error verdict — the customer sees "we're on it,"
 * not "that failed," because nothing is wrong on their end.
 */
export const VERIFY_RETRYING_COPY = {
  headline: 'Verifying your connection.',
  body:
    'This can take a few minutes right after the script runs. Guardian keeps checking on its own. Nothing more to do.',
} as const

/**
 * Copy for the "finish mail coverage" affordance shown on the ExoProvisioning
 * state. Framed as COMPLETING setup, not an optional extra: mail coverage is
 * required and still finishing, so the customer downloads the mail-included
 * script for their Microsoft 365 admin to run, then verifies again (R9:
 * customer-driven re-download + re-run + re-paste). Jargon-free (swept by
 * allComposedStrings).
 */
export const EXCHANGE_FOLLOWUP_COPY = {
  label: 'Finish mail coverage',
  helper:
    'Mail coverage still needs one more step to finish. Download this script for your Microsoft 365 admin to run, then verify again. That finishes the Exchange part of setup.',
  toast: 'Mail-coverage script downloaded. Hand it to your Microsoft 365 admin to run, then verify again to finish setup.',
} as const

/* -------------------------------------------------------------------------- */
/* License gate — the Layer-2 pre-flight soft-block on the connect step        */
/* -------------------------------------------------------------------------- */

/**
 * View model for the connect step's license soft-block banner. Rendered by
 * SetupJourney ONLY while the gate is composed and not yet dismissed; the
 * "proceed anyway" escape reveals the normal download flow (the customer may
 * know a license purchase is already in flight).
 */
export interface LicenseGate {
  headline: string
  body: string
  proceedLabel: string
  proceedHelper: string
}

/**
 * Copy for the license soft-block (advisor voice; swept for jargon). Names the
 * qualifying Microsoft plans in customer terms only — never the service or
 * API internals — and points at the free trial as the fastest unblock.
 */
export const LICENSE_GATE_COPY: LicenseGate = {
  headline: 'Your Microsoft plan needs one more license first.',
  body:
    'Guardian checked your Microsoft 365 subscription, and it doesn’t yet include what Microsoft requires for continuous configuration monitoring. Any of these unlocks it: Microsoft 365 E3 or E5, Microsoft 365 Business Premium, or Microsoft Entra ID P1. The free Microsoft Entra ID P1 trial in the Microsoft admin center works too, and your Microsoft admin can activate it in a few minutes.',
  proceedLabel: 'Proceed anyway',
  proceedHelper:
    'You can still download and run the setup script now. The final connection check won’t pass until a qualifying license is in place.',
} as const

/**
 * Pure gate decision: the banner composes ONLY on a definite "ineligible".
 * 'eligible' and 'unknown' (the check itself failed server-side) both return
 * null — fail open, never block setup on a broken pre-flight. Null/undefined
 * input (query still loading or errored) is likewise null.
 */
export function composeLicenseGate(
  eligibility: TcmLicenseEligibility | null | undefined,
): LicenseGate | null {
  return eligibility?.eligibility === 'ineligible' ? LICENSE_GATE_COPY : null
}

/* -------------------------------------------------------------------------- */
/* SCOPE_PROFILES — the five canonical coverage profiles                       */
/* -------------------------------------------------------------------------- */

export type ScopeProfileId =
  | 'identity-access'
  | 'threat-protection-addon'
  | 'endpoint-addon'
  | 'collaboration-addon'
  | 'governance-optin'

export interface ScopeProfile {
  /** Server profile name — wire contract with POST …/scope { profiles: [id] }. */
  id: ScopeProfileId
  name: string
  description: string
  /** base = always included; default = recommended; addon/optin = optional. */
  kind: 'base' | 'default' | 'addon' | 'optin'
  /** Monitoring groups the profile bundles — used to sum sized counts. */
  groups: TcmMonitoringGroup[]
  /** Chips start selected for base + the recommended default. */
  preselected: boolean
  /**
   * Short lower-case label for the picker's plain-language read-back
   * ("Watching identity and threat protection.") — the name minus its
   * "& access"/"& device" qualifier so the sentence reads naturally.
   */
  summaryLabel: string
}

/**
 * Static mirror of `Policies/common/tcm_monitoring_profiles.json` profile
 * names/groups with customer-voiced copy. A test pins the ids so a
 * server-side rename breaks loudly. The governance chip renders only when
 * `coverage.purviewOptIn` (composeScopeChips enforces it).
 *
 * The five chips are now SINGLE-GROUP: each monitorable workload maps 1:1 to a
 * chip, so a customer widens or narrows coverage one workload at a time and the
 * live capacity meter can attribute every setting to exactly one chip (no
 * cross-group double-count). The recommended default — the chips that start
 * preselected — is identity-access + threat-protection-addon, the customer-named
 * "Security Essentials" (identity + threat protection). The customer widens from
 * there by choice (endpoint, collaboration); governance stays opt-in and renders
 * only when `purviewOptIn`. composeScopeChips already sums one group per chip, so
 * it keeps working unchanged for these single-group profiles.
 */
export const SCOPE_PROFILES: readonly ScopeProfile[] = [
  {
    id: 'identity-access',
    name: 'Identity & access',
    description: 'Who can get in, from where, and how strongly they must prove it.',
    kind: 'base',
    groups: ['identity'],
    preselected: true,
    summaryLabel: 'identity',
  },
  {
    id: 'threat-protection-addon',
    name: 'Threat protection',
    description:
      'The controls that stop threats getting in and bad mail or data getting out.',
    kind: 'addon',
    groups: ['threatProtection'],
    preselected: true,
    summaryLabel: 'threat protection',
  },
  {
    id: 'endpoint-addon',
    name: 'Endpoint & device',
    description: 'Whether your managed devices are held to the security bar.',
    kind: 'addon',
    groups: ['endpoint'],
    preselected: false,
    summaryLabel: 'endpoint',
  },
  {
    id: 'collaboration-addon',
    name: 'Collaboration',
    description: 'How open Teams is to the outside world.',
    kind: 'addon',
    groups: ['collaboration'],
    preselected: false,
    summaryLabel: 'collaboration',
  },
  {
    id: 'governance-optin',
    name: 'Governance & compliance',
    description: 'Data protection, retention, and privileged-access policy.',
    kind: 'optin',
    groups: ['governance'],
    preselected: false,
    summaryLabel: 'governance',
  },
] as const

export interface ScopeChip {
  id: ScopeProfileId
  name: string
  description: string
  kind: ScopeProfile['kind']
  preselected: boolean
  /**
   * "171 settings" summed from the sizing capture's per-group counts; null
   * when the capture hasn't sized this profile's groups (never a faked 0 —
   * a genuine measured 0 renders honestly as "0 settings").
   */
  countLabel: string | null
}

/**
 * The scope step's chips: SCOPE_PROFILES with real counts from the sizing
 * capture (blueprint D3 — counts come from the size-status response, not the
 * coverage DTO). Governance renders only for tenants with the governance
 * opt-in already in place.
 */
export function composeScopeChips(
  sizedGroups: TcmSizedGroup[] | null,
  purviewOptIn: boolean,
): ScopeChip[] {
  const countsByGroup = new Map<TcmMonitoringGroup, number>()
  for (const g of sizedGroups ?? []) {
    countsByGroup.set(g.group, g.instanceCount)
  }

  return SCOPE_PROFILES.filter(p => p.id !== 'governance-optin' || purviewOptIn).map(p => {
    let sum = 0
    let sampled = false
    for (const group of p.groups) {
      const count = countsByGroup.get(group)
      if (count != null) {
        sampled = true
        sum += count
      }
    }
    return {
      id: p.id,
      name: p.name,
      description: p.description,
      kind: p.kind,
      preselected: p.preselected,
      countLabel: sampled ? formatSettingCount(sum) : null,
    }
  })
}

/* -------------------------------------------------------------------------- */
/* composeCapacityMeter — the live "how much of your watch this fills" verdict  */
/* -------------------------------------------------------------------------- */

export interface CapacityMeter {
  /** Total settings the current selection adds up to; null until we've sized. */
  selectedCount: number | null
  /** How much continuous watch can follow closely, in settings. */
  ceiling: number
  /** selectedCount / ceiling; null until we've sized. Drives the bar fill. */
  fraction: number | null
  /**
   * 'unsized' — sizing hasn't archived yet, so we show no number.
   * 'fits'    — comfortably inside what continuous watch can follow.
   * 'tight'   — close to the most it can follow closely.
   * 'over'    — past it; the customer must remove a workload to continue.
   */
  state: 'unsized' | 'fits' | 'tight' | 'over'
  /** Six-words-or-fewer lead: the count line (or the sizing verdict). */
  headline: string
  /** One supporting sentence — the verdict beneath the count; '' when unsized. */
  detail: string
  /** True only when 'over' — the submit stays inert until a workload comes off. */
  blocked: boolean
}

/**
 * The live capacity meter shown above the scope chips: how much of the tenant's
 * continuous watch the current selection fills, as an advisor's verdict rather
 * than a raw gauge. Pure over its inputs (same vitest-seam discipline as the
 * rest of this module) so every customer-visible string is jargon-swept.
 *
 * Honesty first: until sizing archives (`sizedGroups` null) we show no number at
 * all — never a fabricated 0 — and read as "we're sizing your scope." Once
 * sized, we sum the DISTINCT monitoring groups the selected chips resolve to
 * (one group per chip today, but de-duped defensively so a future multi-group
 * chip can't double-count), mirroring composeScopeChips's governance gating: the
 * governance group only counts when the tenant has the governance opt-in, even
 * if its chip id somehow rode in the selection. A group missing from the sized
 * set contributes 0 (it simply isn't in this tenant's configuration).
 *
 * The verdict bands — fits (≤80%), tight (>80%–100%), over (>100%) — frame the
 * one-action chain: 'over' blocks the submit and the detail tells the customer
 * to remove a workload, so the meter itself is the guardrail. All copy avoids
 * the capacity nouns the customer surface bans (no budget/ceiling/quota and no
 * "/200"); "of about" carries the comparison in plain English.
 */
export function composeCapacityMeter(
  sizedGroups: TcmSizedGroup[] | null,
  selectedProfileIds: string[],
  instanceCeiling: number,
  purviewOptIn: boolean,
): CapacityMeter {
  // Unsized until sizing archives. A non-positive ceiling (never sent today —
  // the server always sends the 800/4=200 constant) is treated as unsized too,
  // so the function stays total and can't emit a NaN fraction / a spurious 'over'
  // verdict the customer couldn't satisfy.
  if (sizedGroups == null || instanceCeiling <= 0) {
    return {
      selectedCount: null,
      ceiling: instanceCeiling,
      fraction: null,
      state: 'unsized',
      headline: 'We’re sizing your scope.',
      detail: '',
      blocked: false,
    }
  }

  const countsByGroup = new Map<TcmMonitoringGroup, number>()
  for (const g of sizedGroups) {
    countsByGroup.set(g.group, g.instanceCount)
  }

  // Resolve the selected chip ids to a DISTINCT set of monitoring groups via
  // SCOPE_PROFILES (unknown ids are ignored). Governance only counts when the
  // tenant has the opt-in — mirror composeScopeChips's gating so the meter and
  // the chips agree on what's actually selectable.
  const selectedGroups = new Set<TcmMonitoringGroup>()
  for (const profile of SCOPE_PROFILES) {
    if (!isSelectedSelectable(profile, selectedProfileIds, purviewOptIn)) continue
    for (const group of profile.groups) selectedGroups.add(group)
  }

  let selectedCount = 0
  for (const group of selectedGroups) {
    selectedCount += countsByGroup.get(group) ?? 0
  }

  const fraction = selectedCount / instanceCeiling
  const state: CapacityMeter['state'] = fraction > 1.0 ? 'over' : fraction > 0.8 ? 'tight' : 'fits'

  // Count line built without a slash (the "/200" tell is banned) — "of about"
  // carries the comparison in plain English.
  const headline = selectedCount + ' of about ' + instanceCeiling + ' settings'
  const detail =
    state === 'over'
      ? composeOverDetail(countsByGroup, selectedProfileIds, purviewOptIn)
      : state === 'tight'
        ? 'Close to the most continuous watch can follow closely.'
        : 'Comfortably within continuous watch.'

  return {
    selectedCount,
    ceiling: instanceCeiling,
    fraction,
    state,
    headline,
    detail,
    blocked: state === 'over',
  }
}

/**
 * The single "does this selected profile actually count?" gate, shared by the
 * capacity meter's group union and the over-verdict's per-profile sums so the
 * gauge total and the recommended trim can never disagree: a profile counts
 * only when it is selected AND (for governance) the tenant carries the opt-in.
 */
function isSelectedSelectable(
  profile: ScopeProfile,
  selectedProfileIds: string[],
  purviewOptIn: boolean,
): boolean {
  if (!selectedProfileIds.includes(profile.id)) return false
  if (profile.id === 'governance-optin' && !purviewOptIn) return false
  return true
}

/**
 * The over-state verdict with a recommended trim: instead of a bare "remove a
 * workload", name the LARGEST removable selected workload — the one whose
 * removal moves the gauge the most — so the customer's next click is obvious.
 * Base coverage can't come off, so when the base alone is past what continuous
 * watch can follow (no removable workload selected), the honest verdict is
 * that Guardian's team tailors it — there is no self-serve action.
 */
function composeOverDetail(
  countsByGroup: Map<TcmMonitoringGroup, number>,
  selectedProfileIds: string[],
  purviewOptIn: boolean,
): string {
  let biggest: { label: string; count: number } | null = null
  for (const profile of SCOPE_PROFILES) {
    if (profile.kind === 'base') continue
    if (!isSelectedSelectable(profile, selectedProfileIds, purviewOptIn)) continue
    let count = 0
    for (const group of profile.groups) count += countsByGroup.get(group) ?? 0
    if (count > 0 && (biggest == null || count > biggest.count)) {
      biggest = { label: profile.summaryLabel, count }
    }
  }
  if (biggest == null) {
    return (
      'Your configuration is bigger than continuous watch can follow closely on its own. ' +
      'Your Guardian team will tailor this with you.'
    )
  }
  return (
    'More than continuous watch can follow closely. ' +
    `Start by removing ${biggest.label} (${biggest.count} settings). You can widen coverage again later.`
  )
}

/* -------------------------------------------------------------------------- */
/* Scope picker modal — copy + composed read-backs                             */
/* -------------------------------------------------------------------------- */

/**
 * Static chrome copy for the "Choose what we watch" modal (the scope step's
 * picker now lives in a dialog, not inline). Headline is split lead + tail;
 * ScopeCoverageModal joins them into one upright title ("Choose what
 * Guardian watches.").
 * All strings are advisor-voiced and jargon-free; the jargon sweep covers them
 * via allComposedStrings(). Dynamic copy — the count line, verdict, and the
 * explainer's capacity figure — is composed elsewhere (composeCapacityMeter,
 * composeScopeExplainer) so nothing here hardcodes a tenant-specific number.
 */
export const SCOPE_MODAL_COPY = {
  eyebrow: 'Coverage · Choose what we watch',
  headline: 'Choose what Guardian',
  headlineEm: 'watches.',
  deck:
    'Start with the essentials and widen any time. The gauge shows how much of your continuous ' +
    'watch the selection fills, and whether it can follow every change closely.',
  confirm: 'Start watching',
  cancel: 'Cancel',
  /** Footer empty state when the customer has deselected everything. */
  emptySummary: 'Nothing selected yet.',
} as const

export interface ScopeExplainerItem {
  /** Bold lead clause (≤ six words). */
  lead: string
  /** One supporting sentence. */
  body: string
}

export interface ScopeExplainer {
  toggleLabel: string
  items: ScopeExplainerItem[]
}

/**
 * The collapsible "How these numbers work" explainer inside the picker. Answers
 * the three honest questions: where the counts come from (a full configuration
 * capture, not an estimate), what the capacity figure means (how closely we can
 * watch; since wave 3 it is the tenant's own room, so it is not the same for
 * every tenant), and how fresh they are (most-recent
 * capture; re-size to refresh; a recurring rhythm once live).
 *
 * The capacity figure is interpolated from the live `instanceCeiling` rather
 * than hardcoded, so it can never drift from the gauge above it. Crucially it
 * names NO sweep cadence — that figure is sized per-tenant and kept internal
 * (the channel copy in monitoring-coverage.ts made the same deliberate choice,
 * codex P2 2026-06-16); "a recurring rhythm that adapts to your size" is the
 * approved customer phrasing.
 */
export function composeScopeExplainer(instanceCeiling: number): ScopeExplainer {
  return {
    toggleLabel: 'How these numbers work',
    items: [
      {
        lead: 'The counts are your real settings.',
        body:
          'Each one is how many settings of that kind exist in your tenant today, measured from a ' +
          'full capture of your configuration, not an estimate.',
      },
      {
        lead: `About ${instanceCeiling} is how closely we can watch.`,
        body:
          'It’s the most settings continuous watch can follow and re-check often. It measures how ' +
          'closely we watch, not how much you have.',
      },
      {
        lead: 'They stay current.',
        body:
          'The counts come from your most recent capture. Re-size any time to refresh them before you ' +
          'switch on. Once watch is live, Guardian re-captures your configuration on a recurring rhythm ' +
          'that adapts to your size.',
      },
    ],
  }
}

/**
 * Plain-language read-back of the chosen coverage for the modal footer —
 * "identity and threat protection" → the caller renders "Watching … .". Uses
 * each selected profile's short summaryLabel in SCOPE_PROFILES order, joined as
 * a natural English list. Governance is gated behind purviewOptIn (mirrors
 * composeScopeChips + composeCapacityMeter) so a stray selected id for a tenant
 * without the opt-in can't appear. Returns null when nothing is selected, so
 * the footer can show its own empty state rather than a bare "Watching .".
 */
export function composeSelectionSummary(
  selectedProfileIds: string[],
  purviewOptIn: boolean,
): string | null {
  const labels: string[] = []
  for (const profile of SCOPE_PROFILES) {
    if (!selectedProfileIds.includes(profile.id)) continue
    if (profile.id === 'governance-optin' && !purviewOptIn) continue
    labels.push(profile.summaryLabel)
  }
  if (labels.length === 0) return null
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

/* -------------------------------------------------------------------------- */
/* composeScopeDefaultCard — the zero-decision scope step                       */
/* -------------------------------------------------------------------------- */

export interface ScopeDefaultCard {
  /** Verdict-first headline, six words or fewer. */
  headline: string
  /** One supporting sentence naming the current selection and its count. */
  body: string
  /** Primary button label — reuses the picker's own confirm copy. */
  confirmLabel: string
  /** Secondary link that opens the full picker. */
  adjustLabel: string
}

/**
 * Copy for the scope step's default experience: Security Essentials starts
 * pre-selected and the customer sees this one-line advisor card in place of
 * the picker, with the picker one click away behind `adjustLabel` — no
 * decision required to go from Sized to Watching. `selectionSummary` is
 * composeSelectionSummary's read-back of whatever is CURRENTLY selected (the
 * default unless the customer opened the picker and changed it), and
 * `selectedCount` is composeCapacityMeter's already-sized total for that same
 * selection — null pre-sizing, so the count line never fabricates a number.
 */
export function composeScopeDefaultCard(
  selectionSummary: string | null,
  selectedCount: number | null,
): ScopeDefaultCard {
  const countLabel = formatSettingCount(selectedCount)
  const body =
    selectionSummary == null
      ? 'Choose what Guardian should watch, then switch on.'
      : countLabel != null
        ? `Starting with ${selectionSummary}, ${countLabel}. Widen it any time.`
        : `Starting with ${selectionSummary}. Widen it any time.`
  return {
    headline: 'Watching the settings that matter most.',
    body,
    confirmLabel: SCOPE_MODAL_COPY.confirm,
    adjustLabel: 'Adjust coverage',
  }
}

/* -------------------------------------------------------------------------- */
/* Sizing progress copy + toasts + filename                                    */
/* -------------------------------------------------------------------------- */

/** Honest progress copy for the size step's poll states. */
export function composeSizingProgress(status: TcmSizingStatus['status']): {
  headline: string
  body: string
} {
  switch (status) {
    case 'sizing':
      return {
        headline: 'Capture in progress.',
        body:
          'We’re taking a full capture of your configuration. This usually finishes within a few minutes, and this page keeps itself up to date.',
      }
    case 'sized':
      return {
        headline: 'Capture complete.',
        body: 'Your configuration is captured and your coverage options are ready.',
      }
    case 'failed':
    default:
      return {
        headline: 'The capture didn’t finish.',
        body:
          'Nothing was changed in your tenant. Start it again, and if it keeps failing your Guardian team can pick it up from there.',
      }
  }
}

/** Toast copy for the journey mutations (lives here so the jargon sweep covers it). */
export const JOURNEY_TOASTS = {
  scriptDownloaded: 'Script downloaded. Hand it to your Microsoft 365 admin to run.',
  scriptCopied: 'Script copied. Hand it to your Microsoft 365 admin to run.',
  scriptCopyFailed: 'Could not copy. Use the download instead.',
  sizingStarted: 'Capture started. We’ll keep this page up to date.',
  scopeChosen: 'Coverage chosen. One more step and the watch goes live.',
  activated: 'Continuous watch is on. Changes land in the queue from here.',
} as const

/**
 * Client-side download filename for the setup script (the proxy forwards
 * Content-Disposition but axios never applies it — blueprint house review #7).
 */
export function connectScriptFilename(tenantId: string, now: Date = new Date()): string {
  return `Guardian-Onboarding-${tenantId}-${format(now, 'yyyyMMdd')}.ps1`
}
